// Pure helpers for the market-data Edge Function (no Deno APIs, no imports) so they can be unit-tested with Node.
//
// The app's canonical symbol is the Yahoo style: AAPL, BHP.AX (ASX), FPH.NZ (NZX), SHOP.TO (TSX), VOD.L (LSE).
// Each provider names exchanges differently, so we map at the edge and always hand back the canonical symbol.

export type Provider = "twelvedata" | "finnhub" | "eodhd" | "yahoo";
export type Quote = { symbol: string; price: number; currency: string | null; prevClose: number | null; changePct: number | null; name?: string; ts: number; source: Provider };
export type Point = { t: number; price: number };

type Ex = { td: string; eod: string; ccy: string; tz: string; open: [number, number] }; // open: minutes from midnight local
export const EXCHANGES: Record<string, Ex> = {
  AX: { td: "ASX", eod: "AU", ccy: "AUD", tz: "Australia/Sydney", open: [600, 960] },
  NZ: { td: "NZX", eod: "NZ", ccy: "NZD", tz: "Pacific/Auckland", open: [600, 1005] },
  TO: { td: "TSX", eod: "TO", ccy: "CAD", tz: "America/Toronto", open: [570, 960] },
  V: { td: "TSXV", eod: "V", ccy: "CAD", tz: "America/Toronto", open: [570, 960] },
  L: { td: "LSE", eod: "LSE", ccy: "GBP", tz: "Europe/London", open: [480, 990] },
};
const US_TZ = "America/New_York";

export function splitSymbol(sym: string): { base: string; suffix: string } {
  const s = sym.trim().toUpperCase();
  const m = /^(.+)\.([A-Z]{1,2})$/.exec(s);
  // BRK.B / BF.B are share classes on US exchanges, not exchange suffixes
  if (m && EXCHANGES[m[2]]) return { base: m[1], suffix: m[2] };
  return { base: s, suffix: "" };
}
export const isInternational = (sym: string) => splitSymbol(sym).suffix !== "";

// Which providers can serve a symbol, given what the project owner has enabled.
// A US-only plan is never asked for ASX/NZX symbols — that wastes quota and returns nothing.
export function supports(p: Provider, sym: string, flags: { tdIntl?: boolean; finnhubIntl?: boolean } = {}): boolean {
  if (!isInternational(sym)) return true;
  if (p === "twelvedata") return !!flags.tdIntl;
  if (p === "finnhub") return !!flags.finnhubIntl;
  return true; // eodhd and yahoo cover these exchanges
}

export function providerSymbol(p: Provider, sym: string): { symbol: string; exchange?: string } {
  const { base, suffix } = splitSymbol(sym);
  if (!suffix) return { symbol: base };
  const ex = EXCHANGES[suffix];
  if (p === "twelvedata") return { symbol: base, exchange: ex.td };
  if (p === "eodhd") return { symbol: `${base}.${ex.eod}` };
  return { symbol: `${base}.${suffix}` }; // yahoo, finnhub
}

// Map a provider's exchange name back to the canonical suffix ("" = US, null = unsupported/unknown).
export function canonicalSuffix(exchange: string): string | null {
  const e = (exchange || "").toUpperCase();
  for (const [suffix, ex] of Object.entries(EXCHANGES)) if (ex.td === e || ex.eod === e) return suffix;
  if (["NASDAQ", "NYSE", "NYSE ARCA", "NYSE AMERICAN", "AMEX", "BATS", "CBOE", "NMS", "NYQ", "NGM", "PCX", "ASE", "BTS", "NCM", "US"].includes(e)) return "";
  return null;
}

export function marketOpen(sym: string, now = new Date()): boolean {
  const { suffix } = splitSymbol(sym);
  const ex = EXCHANGES[suffix];
  const tz = ex?.tz || US_TZ, [o, c] = ex?.open || [570, 960];
  const parts = new Intl.DateTimeFormat("en-US", { timeZone: tz, weekday: "short", hour: "2-digit", minute: "2-digit", hour12: false }).formatToParts(now);
  const get = (t: string) => parts.find((p) => p.type === t)?.value || "";
  if (["Sat", "Sun"].includes(get("weekday"))) return false;
  const mins = (Number(get("hour")) % 24) * 60 + Number(get("minute"));
  return mins >= o && mins < c;
}

// How long a cached value stays fresh. Open markets: 5 minutes. Closed: until roughly the next session (capped at 3 hours).
export function ttlSeconds(kind: "quote" | "history" | "search" | "fx", sym = "", now = new Date(), openTtl = 300): number {
  if (kind === "search") return 7 * 86400;
  if (kind === "history") return 6 * 3600;
  if (kind === "fx") return 6 * 3600;
  return marketOpen(sym, now) ? openTtl : 3 * 3600;
}
export const isFresh = (fetchedAtMs: number, ttlSec: number, now = Date.now()) => now - fetchedAtMs < ttlSec * 1000;

export const RANGES: Record<string, { yahoo: [string, string]; td: [string, number]; days: number }> = {
  "1W": { yahoo: ["5d", "1d"], td: ["1day", 7], days: 7 },
  "1M": { yahoo: ["1mo", "1d"], td: ["1day", 30], days: 30 },
  "3M": { yahoo: ["3mo", "1d"], td: ["1day", 90], days: 90 },
  "1Y": { yahoo: ["1y", "1wk"], td: ["1week", 52], days: 365 },
  "5Y": { yahoo: ["5y", "1wk"], td: ["1week", 260], days: 1825 },
};

export const num = (v: unknown): number | null => { const n = typeof v === "string" ? parseFloat(v) : (v as number); return typeof n === "number" && isFinite(n) ? n : null; };
export const pct = (price: number, prev: number | null) => (prev && prev > 0 ? ((price - prev) / prev) * 100 : null);
